import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { differenceInDays } from 'date-fns';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import { WidgetDate, WidgetTime, ClientsCancellationsSVG, CrossSVG } from '../../../../components';
import { makeandcheckerService, usersService } from '../../../../services';
import statuses from '../../../../constants/makeandchecker/statuses';
import { FilterQueryParams } from '../../../../helpers';
import '../../../../styles/sass/global/components/checker.scss';
import { Box, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const actionExecute = 'execute';
const actionCancel = 'cancel';
const actions = {
    [actionExecute]: {
        progress: 'Aprobando',
        success: 'aprobo',
        id: 2
    },
    [actionCancel]: {
        progress: 'Rechazando',
        success: 'rechazo',
        id: 3
    }
};

export default function ClientDialogConfirm ({ clientToDrop, show, onHide, user, userRoles, setAlert, setOpenAlert }) {
    const { t } = useTranslation();
    // State
    // Action State

    const [action, setAction] = useState('');

    // Loading State

    const [isLoading, setIsLoading] = useState(true);
    const [names, setNames] = useState({ createdBy: '', idUpdatedBy: '' });
    const isClientNotPending = clientToDrop.status.id !== statuses.pending.id;

    // Functions

    const prepareClient = () => {
        setIsLoading(true);
        getNames();
        clientToDrop.diffDays = differenceInDays(moment(clientToDrop.created_at).toDate(), new Date());
        clientToDrop.diffDaysText = !clientToDrop.diffDays ? 'Hoy' : `Hace ${-clientToDrop.diffDays} día${-clientToDrop.diffDays > 1 ? 's' : ''}`;

        setIsLoading(false);
    };

    const getNames = () => {
        const filters = new FilterQueryParams();
        const options = {
            order: 'id-desc',
            pageSize: 2
        };
        filters.in('id', [clientToDrop.metadata?.idCreatedBy, clientToDrop.metadata?.idUpdatedBy]);
        options.filters = filters.build();

        usersService.findSmall(options)
            .then(res => {
                const createdBy = res.find(user => user.id === clientToDrop.metadata?.idCreatedBy).formattedName;
                let idUpdatedBy;
                if (clientToDrop.metadata.idUpdatedBy) {
                    idUpdatedBy = res.find(user => user.id === clientToDrop.metadata?.idUpdatedBy).formattedName;
                }
                setNames({ createdBy, idUpdatedBy });
            })
            .catch(e => {
                console.error(e);
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: 'Algo no salió como esperamos',
                    description: 'Lo sentimos, en este momento no fue posible completar la operación.'
                }));
                setOpenAlert(true);
            });
    };

    const executeDrop = () => callDrop(actionExecute);

    const cancelDrop = () => callDrop(actionCancel);

    const callDrop = action => {
        const options = actions[action];

        setAction(action);
        setIsLoading(true);

        const data = {
            idStatus: options.id
        };

        makeandcheckerService
            .toggle(clientToDrop.id, data)
            .then(() => {
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'warning',
                    description: (
                        <Box display='flex' flexDirection='row' alignItems='baseline'>
                            <Typography variant='subtitle2'>Se {options.success} la baja del cliente </Typography>
                            <Box margin= '0px 2px 0px 2px'/>
                            <Typography variant='subtitle1'>{clientToDrop.client.formattedName}</Typography>
                        </Box>
                    ),
                    className: 'clientsMC_Alert--warning'
                }));
                setOpenAlert(true);
            })
            .catch(e => {
                setAlert((prevAlert) => ({
                    ...prevAlert,
                    severity: 'error',
                    title: 'Algo no salió como esperamos',
                    description: 'Lo sentimos, en este momento no fue posible completar la operación.',
                    className: 'clientsMC_Alert--error'
                }));
                setOpenAlert(true);
                console.error(`Error on ${action} adjustment`, e);
            })
            .finally(() => {
                setIsLoading(false);
                onHide(true);
            });
    };

    useEffect(() => {
        if (!clientToDrop) {
            onHide();
            return;
        }

        prepareClient();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [clientToDrop]);

    // Main Component
    return (
        <Dialog open={show} onClose={() => onHide(false)} style={{ fontFamily: 'Roboto' }} PaperProps={{ style: { minWidth: '610px', minHeight: '326', borderRadius: '12px' } }}>
            <DialogTitle>
                {!isLoading && (
                    <Box>
                        <Box
                            display='flex'
                            alignItems='center'
                            justifyContent='space-between'
                        >
                            <Box
                                display='flex'
                                alignItems='center'
                            >
                                <ClientsCancellationsSVG/>
                                <Typography variant= 'h6' style={{ marginLeft: '6px' }}>{t('management.unsubscription.table.details.modal.title')}</Typography>
                            </Box>
                            <Button onClick={() => onHide(false)} className='crossIcon'>
                                <CrossSVG/>
                            </Button>
                        </Box>
                        <br />
                        <Box style={{ marginTop: '3px', borderBottom: '1px solid #F95623', paddingBottom: '10px' }}>
                            <Box className='dialogFields'>
                                <Stack style={{ marginRight: '9px' }}>
                                    <Typography variant='subtitle1' >{t('management.unsubscription.table.details.modal.managed')}</Typography>
                                    <Typography variant='body1'>{`${names?.createdBy}`}</Typography>
                                </Stack>
                                <Stack style={{ marginRight: '15px' }}>
                                    <Typography variant='subtitle1' style={{ marginRight: '3px' }}>{t('management.unsubscription.table.details.modal.date')}</Typography>
                                    <Typography variant='body1'>
                                        <WidgetDate date={new Date(clientToDrop.created_at)} />
                                        {' '}
                                        <WidgetTime date={new Date(clientToDrop.created_at)} />
                                        {' Hs'}
                                    </Typography>
                                </Stack>
                            </Box>
                        </Box>
                        {names?.idUpdatedBy &&
              (<Box style={{ marginTop: '12px', borderBottom: '1px solid #F95623', paddingBottom: '10px' }}>
                  <Box className='dialogFields'>
                      <Stack style={{ marginRight: '9px' }}>
                          <Typography variant='subtitle1'>{t(`management.unsubscription.table.status.${statuses[clientToDrop.status.description]?.id}`) + ' ' + t('management.unsubscription.table.details.by')}</Typography>
                          <Typography variant='body1'>{`${names?.idUpdatedBy}`}</Typography>
                      </Stack>
                      <Stack style={{ marginRight: '15px' }}>
                          <Typography variant='subtitle1' style={{ marginRight: '3px' }}>{t('management.unsubscription.table.details.modal.date')}</Typography>
                          <Typography variant='body1'>
                              <WidgetDate date={new Date(clientToDrop.updated_at)} />
                              {' '}
                              <WidgetTime date={new Date(clientToDrop.updated_at)} />
                              {' Hs'}
                          </Typography>
                      </Stack>
                  </Box>
              </Box>
              )
                        }
                    </Box>
                )}
                {(isLoading && !action) && <Typography>{t('loading')}</Typography>}
            </DialogTitle>
            <DialogContent>
                {!isLoading && (
                    <Box>
                        <Box className='dialogData'>
                            <Box className= 'borderBottom' style={{ marginRight: '9px' }} >
                                <Typography variant='body2'>{t('management.unsubscription.table.details.modal.client')}</Typography>
                                <Typography variant='subtitle1' component='span'>{clientToDrop?.client?.formattedName || ''}</Typography>
                            </Box>
                            <Box className='borderBottom'>
                                <Typography variant='body2'>{t('management.unsubscription.table.details.modal.unsubscriptionDate')}</Typography>
                                <Typography variant='subtitle1'> {clientToDrop?.client?.created_at ? (<WidgetDate date={new Date(clientToDrop?.client?.created_at)} />) : ''}</Typography>
                            </Box>
                        </Box>
                        <Box className='dialogData' style={{ marginTop: '6px' }}>
                            <Box className= 'borderBottom' style={{ marginTop: '9px' }}>
                                <Typography variant='body2'>{t('management.unsubscription.table.details.modal.document')}</Typography>
                                <Typography variant='subtitle1'>{clientToDrop?.client?.person?.document?.description || '-'}</Typography>
                            </Box>
                            <Box className='borderBottom' style={{ margin: '9px 0px 0px 9px' }}>
                                <Typography variant='body2'>{t('management.unsubscription.table.details.modal.reason')}</Typography>
                                <Typography variant='subtitle1'>{clientToDrop?.metadata?.body?.reason}</Typography>
                            </Box>
                        </Box>
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Box style={{ marginTop: '18px' }}>
                    {(!isClientNotPending && !action) && (
                        <Button
                            hidden={userRoles.operator}
                            disabled={isLoading || isClientNotPending}
                            variant='outlined'
                            style={{
                                backgroundColor: '#B71E15',
                                color: 'white',
                                borderRadius: '50px',
                                textTransform: 'none',
                                marginRight: '16px'
                            }}
                            onClick={cancelDrop} >
                            {isLoading && action === actionExecute ? <CircularProgress size={24} color="inherit" /> : t('buttons.reject')}
                        </Button>)}
                    {'  '}
                    {(!isClientNotPending && !action) && (
                        <Button
                            hidden={userRoles.operator}
                            disabled={isLoading || isClientNotPending}
                            variant='outlined'
                            style={{
                                backgroundColor: '#1AC740',
                                color: 'white',
                                borderRadius: '50px',
                                textTransform: 'none',
                                marginRight: '28px'
                            }}
                            onClick={executeDrop} >
                            {isLoading && action === actionExecute ? <CircularProgress size={24} color="inherit" /> : t('buttons.approve')}
                        </Button>)}
                    {isClientNotPending &&
                    (<Button
                        disabled={isLoading}
                        onClick={() => onHide(false)}
                        style={{
                            height: '36px',
                            width: '88px',
                            backgroundColor: '#F95623',
                            color: 'white',
                            borderRadius: '50px',
                            textTransform: 'none',
                            marginRight: '28px'
                        }}
                    >{t('buttons.accept')}</Button>)}
                </Box>
            </DialogActions>
        </Dialog>
    );
}
